import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import {
  faCircleCheck,
  faCircleXmark,
  faClipboardCheck,
  faClock,
  faListCheck,
  faSitemap,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import List, { type ListItem } from "../../../components/List/List"
import { EEnableStatusType } from "../../../Enum/EEnableStatusType"
import { useChecklist } from "../../../hooks/Checklist/useChecklist"
import type { ChecklistTemplate } from "../../../Objects/Checklist/ChecklistTemplate"
import type { ChecklistSector } from "../../../Objects/Checklist/ChecklistSector"
import { ChecklistSectorsPopup } from "./ChecklistSectorsPopup"
import { ChecklistTemplatePopup } from "./ChecklistTemplatePopup"
import { ChecklistHistoryPopup } from "./ChecklistHistoryPopup"

const quickActionPopups = {
  "checklists-sectors": "sectors",
  "checklists-history": "history",
} as const

export const ChecklistPage: React.FC = () => {
  const { t } = useTranslation(["pages", "general"])

  const [searchParams] = useSearchParams()

  const { getTemplates, getSectors } = useChecklist()

  const [templates, setTemplates] = useState<ChecklistTemplate[]>([])

  const [sectors, setSectors] = useState<ChecklistSector[]>([])

  const [editingTemplate, setEditingTemplate] = useState<ChecklistTemplate | null | undefined>(undefined)

  const [showSectors, setShowSectors] = useState(false)

  const [showHistory, setShowHistory] = useState(false)

  const load = useCallback(async () => {
    const [templatesResult, sectorsResult] = await Promise.all([getTemplates(true), getSectors()])

    if (templatesResult) setTemplates(templatesResult)

    if (sectorsResult) setSectors(sectorsResult)
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    const quickAction = searchParams.get("quickAction")

    if (quickAction && quickAction in quickActionPopups) {
      const target = quickActionPopups[quickAction as keyof typeof quickActionPopups]

      if (target === "sectors") setShowSectors(true)

      if (target === "history") setShowHistory(true)
    }

    if (searchParams.get("onboardingCreate") === "true") {
      setEditingTemplate(null)
    }
  }, [searchParams])

  const sectorColorById = new Map(sectors.map((sector) => [sector.id, sector.colorHex || "#f97316"]))

  const items: ListItem[] = templates.map((template, index) => {
    const enable = template.isActive

    const accentColor = enable
      ? (template.sectorId ? sectorColorById.get(template.sectorId) : undefined) ?? "#f97316"
      : "#94a3b8"

    return {
      isDisable: !enable,
      id: template.id,
      index,
      title: template.name,
      linkImage: "",
      dataFilter: [
        template.name,
        template.sectorName ?? "",
        enable ? "" : t("general:label_inactive"),
        t(`checklists.templates.frequency${template.frequencyType}`),
      ],
      children: (
        <div
          className={`relative flex flex-col gap-3 p-4 pl-5 rounded-2xl overflow-hidden border transition-all duration-300 ${
            enable
              ? "bg-white border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5"
              : "bg-gray-50 border-gray-100 opacity-70"
          }`}
        >
          <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{ backgroundColor: accentColor }} />

          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3 min-w-0 flex-1">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-white shadow-sm"
                style={{ backgroundColor: accentColor }}
              >
                <FontAwesomeIcon icon={faClipboardCheck} className="text-lg" />
              </div>

              <div className="flex flex-col justify-center min-w-0 flex-1">
                <span className="font-extrabold text-gray-900 text-lg truncate">{template.name}</span>

                <span className="text-sm text-gray-500 font-medium truncate flex items-center gap-1.5">
                  <FontAwesomeIcon icon={faSitemap} className="text-[10px]" />
                  {template.sectorName ?? t("checklists.templates.noSector")}
                </span>
              </div>
            </div>

            {enable ? (
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-100 text-green-700 whitespace-nowrap flex items-center gap-1.5">
                <FontAwesomeIcon icon={faCircleCheck} />
                {t("general:label_active")}
              </span>
            ) : (
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-red-100 text-red-600 whitespace-nowrap flex items-center gap-1.5">
                <FontAwesomeIcon icon={faCircleXmark} />
                {t("general:label_inactive")}
              </span>
            )}
          </div>
        </div>
      ),
      body: (
        <div className="px-4 pb-4 pl-5">
          <div className="border-t border-gray-100 pt-3 flex flex-wrap gap-2">
            <span className="px-2.5 py-1 text-xs rounded-full font-semibold bg-slate-100 text-slate-700 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faClock} />
              {t(`checklists.templates.frequency${template.frequencyType}`)}
            </span>

            {template.isMandatory && (
              <span className="px-2.5 py-1 text-xs rounded-full font-semibold bg-red-100 text-red-700 flex items-center gap-1.5">
                <FontAwesomeIcon icon={faTriangleExclamation} />
                {t("checklists.templates.mandatoryTag")}
              </span>
            )}

            <span className="px-2.5 py-1 text-xs rounded-full font-semibold bg-orange-100 text-orange-700 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faListCheck} />
              {t("checklists.templates.stepsCount", { count: template.steps.length })}
            </span>
          </div>
        </div>
      ),
    }
  })

  return (
    <div className={defaultStyle.containerList}>
      <List
        enableLocalPagination
        messageCount={t("checklists.templates.listCount")}
        messageEmpty={t("checklists.templates.listEmpty")}
        nameFindSearch={t("checklists.templates.searchPlaceholder")}
        data={items}
        nameDropdown={Object.mapEnumToStrings(EEnableStatusType)}
        handleAddButton={() => setEditingTemplate(null)}
        handleRefreshButton={load}
        handleOnClick={(id) => setEditingTemplate(templates.find((e) => e.id === id) ?? null)}
        handleExtraFloatButton={() => setShowSectors(true)}
        extraFloatButtonIcon={faSitemap}
        extraFloatButtonDescriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistpage.sectors"
        handleSecondaryExtraFloatButton={() => setShowHistory(true)}
        secondaryExtraFloatButtonIcon={faClipboardCheck}
        secondaryExtraFloatButtonDescriptionKey="interactionDescriptions:pages.myRestaurant.checklist.checklistpage.history"
      />

      {editingTemplate !== undefined && (
        <ChecklistTemplatePopup
          template={editingTemplate}
          sectors={sectors}
          onClose={() => setEditingTemplate(undefined)}
          onSaved={load}
        />
      )}

      {showSectors && <ChecklistSectorsPopup onClose={() => setShowSectors(false)} onChanged={load} />}

      {showHistory && (
        <ChecklistHistoryPopup templates={templates} sectors={sectors} onClose={() => setShowHistory(false)} />
      )}
    </div>
  )
}
